/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings, Save, Upload, Trash2, Building, CreditCard,
  FileText, CheckCircle, AlertTriangle, Smartphone, Info,
  Eye, EyeOff, Lock, Banknote, Globe
} from 'lucide-react';
import {
  useInvoiceSettings,
  useUpdateInvoiceSettings,
  useUploadSignatureImage,
  useDeleteSignatureImage,
} from '@/hooks/api/useInvoiceSettings';
import { UpdateInvoiceSettingsRequest, PlatformBillingConfig } from '@/types/superadmin.types';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';

export default function InvoiceSettingsPage() {
  const { t } = useTranslation('platform');
  const { data: settings, isLoading } = useInvoiceSettings();
  const updateMutation = useUpdateInvoiceSettings();
  const uploadSignatureMutation = useUploadSignatureImage();
  const deleteSignatureMutation = useDeleteSignatureImage();

  const [formData, setFormData] = useState<UpdateInvoiceSettingsRequest>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  // Get platform billing config from settings
  const billingConfig: PlatformBillingConfig | undefined = settings?.platformBillingConfig;

  // Load settings into form
  useEffect(() => {
    if (settings) {
      setFormData({
        // OHADA
        companyName: settings.companyName || '',
        legalForm: settings.legalForm || '',
        capitalAmount: settings.capitalAmount || undefined,
        capitalCurrency: settings.capitalCurrency || 'GNF',
        rccmNumber: settings.rccmNumber || '',
        placeOfRegistration: settings.placeOfRegistration || '',
        taxId: settings.taxId || '',
        address: settings.address || '',
        city: settings.city || '',
        country: settings.country || 'Guinea',
        postalCode: settings.postalCode || '',
        phone: settings.phone || '',
        email: settings.email || '',
        website: settings.website || '',
        // Payment Instructions (editable)
        paymentInstructions: settings.paymentInstructions || '',
        // Display Toggles
        showMobileMoneySection: settings.showMobileMoneySection ?? true,
        showBankTransferSection: settings.showBankTransferSection ?? true,
        // Signature
        signatureText: settings.signatureText || '',
        signatoryName: settings.signatoryName || '',
        signatoryTitle: settings.signatoryTitle || '',
        // Display Preferences
        showOhadaSection: settings.showOhadaSection ?? true,
        showSignature: settings.showSignature ?? true,
        footerNotesFr: settings.footerNotesFr || '',
        footerNotesEn: settings.footerNotesEn || '',
      });
    }
  }, [settings]);

  const handleInputChange = (field: keyof UpdateInvoiceSettingsRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadSignature = async () => {
    if (!selectedFile) return;

    try {
      setErrorMessage(null);
      await uploadSignatureMutation.mutateAsync(selectedFile);
      setSuccessMessage(t('invoiceSettings.messages.signatureUploaded'));
      setSelectedFile(null);
      setFilePreview(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const error = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('invoiceSettings.messages.uploadFailed') };
      setErrorMessage(error.message);
    }
  };

  const handleDeleteSignature = async () => {
    if (!confirm(t('invoiceSettings.messages.confirmDeleteSignature'))) return;

    try {
      setErrorMessage(null);
      await deleteSignatureMutation.mutateAsync();
      setSuccessMessage(t('invoiceSettings.messages.signatureDeleted'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const error = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('invoiceSettings.messages.deleteFailed') };
      setErrorMessage(error.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setErrorMessage(null);
      await updateMutation.mutateAsync(formData);
      setSuccessMessage(t('invoiceSettings.messages.settingsUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const error = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('invoiceSettings.messages.updateFailed') };
      setErrorMessage(error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-muted/20 rounded"></div>
        <div className="h-96 bg-muted/20 rounded-xl"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Settings className="w-6 h-6" /> {t('invoiceSettings.title')}
        </h1>
        <p className="text-sm text-muted">{t('invoiceSettings.subtitle')}</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/15 border border-emerald-500/30 rounded-lg text-emerald-500">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/15 border border-red-500/30 rounded-lg text-red-500">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{errorMessage}</span>
        </div>
      )}

      {/* Payment Methods Section - Read-Only with Toggles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bank Transfer Card */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          {/* Card Header with Toggle */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 px-6 py-4 border-b border-border-subtle">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{t('invoiceSettings.bankTransfer.title')}</h2>
                  <p className="text-xs text-muted">{t('invoiceSettings.bankTransfer.subtitle')}</p>
                </div>
              </div>
              {/* Toggle Switch */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.showBankTransferSection ?? true}
                  onChange={(e) => handleInputChange('showBankTransferSection', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-muted/30 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-emerald-500"></div>
                <span className="ms-3 text-sm font-medium text-muted flex items-center gap-1">
                  {formData.showBankTransferSection ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {formData.showBankTransferSection ? t('invoiceSettings.visibility.visible') : t('invoiceSettings.visibility.hidden')}
                </span>
              </label>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-6">
            {billingConfig?.bankConfigured ? (
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="flex items-start gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <Lock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-400">
                    {t('invoiceSettings.bankTransfer.configuredInfo')}
                  </p>
                </div>

                {/* Bank Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">{t('invoiceSettings.bankTransfer.bankName')}</label>
                    <div className="px-3 py-2 bg-background/50 border border-border-subtle rounded-lg text-foreground text-sm">
                      {billingConfig.bankName || '—'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">{t('invoiceSettings.bankTransfer.accountHolder')}</label>
                    <div className="px-3 py-2 bg-background/50 border border-border-subtle rounded-lg text-foreground text-sm">
                      {billingConfig.bankAccountHolder || '—'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">{t('invoiceSettings.bankTransfer.accountNumber')}</label>
                    <div className="px-3 py-2 bg-background/50 border border-border-subtle rounded-lg text-foreground text-sm font-mono">
                      {billingConfig.bankAccountNumber || '—'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">{t('invoiceSettings.bankTransfer.swiftBic')}</label>
                    <div className="px-3 py-2 bg-background/50 border border-border-subtle rounded-lg text-foreground text-sm font-mono">
                      {billingConfig.bankSwift || '—'}
                    </div>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-xs font-medium text-muted uppercase tracking-wide">{t('invoiceSettings.bankTransfer.iban')}</label>
                    <div className="px-3 py-2 bg-background/50 border border-border-subtle rounded-lg text-foreground text-sm font-mono">
                      {billingConfig.bankIban || '—'}
                    </div>
                  </div>
                </div>

                {/* Payment Instructions (Editable) */}
                <div className="pt-4 border-t border-border-subtle">
                  <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.bankTransfer.paymentInstructions')}</label>
                  <textarea
                    value={formData.paymentInstructions || ''}
                    onChange={(e) => handleInputChange('paymentInstructions', e.target.value)}
                    rows={2}
                    placeholder={t('invoiceSettings.bankTransfer.paymentInstructionsPlaceholder')}
                    className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/20 flex items-center justify-center">
                  <CreditCard className="w-8 h-8 text-muted" />
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">{t('invoiceSettings.bankTransfer.notConfigured.title')}</h3>
                <p className="text-xs text-muted max-w-xs mx-auto">
                  {t('invoiceSettings.bankTransfer.notConfigured.description')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Money Card */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          {/* Card Header with Toggle */}
          <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/5 px-6 py-4 border-b border-border-subtle">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{t('invoiceSettings.mobileMoney.title')}</h2>
                  <p className="text-xs text-muted">{t('invoiceSettings.mobileMoney.subtitle')}</p>
                </div>
              </div>
              {/* Toggle Switch */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.showMobileMoneySection ?? true}
                  onChange={(e) => handleInputChange('showMobileMoneySection', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-muted/30 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500"></div>
                <span className="ms-3 text-sm font-medium text-muted flex items-center gap-1">
                  {formData.showMobileMoneySection ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {formData.showMobileMoneySection ? t('invoiceSettings.visibility.visible') : t('invoiceSettings.visibility.hidden')}
                </span>
              </label>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-6">
            {billingConfig?.mobileMoneyConfigured ? (
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="flex items-start gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <Lock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-400">
                    {t('invoiceSettings.mobileMoney.configuredInfo')}
                  </p>
                </div>

                {/* Mobile Money Numbers */}
                <div className="space-y-4">
                  {billingConfig.orangeMoneyNumber && (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-orange-500">OM</span>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-orange-400 uppercase tracking-wide">{t('invoiceSettings.mobileMoney.orangeMoney')}</div>
                        <div className="text-lg font-semibold text-foreground font-mono">
                          {billingConfig.orangeMoneyNumber}
                        </div>
                      </div>
                    </div>
                  )}

                  {billingConfig.mtnMomoNumber && (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/20 rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-yellow-500">MTN</span>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-yellow-400 uppercase tracking-wide">{t('invoiceSettings.mobileMoney.mtnMomo')}</div>
                        <div className="text-lg font-semibold text-foreground font-mono">
                          {billingConfig.mtnMomoNumber}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/20 flex items-center justify-center">
                  <Smartphone className="w-8 h-8 text-muted" />
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">{t('invoiceSettings.mobileMoney.notConfigured.title')}</h3>
                <p className="text-xs text-muted max-w-xs mx-auto">
                  {t('invoiceSettings.mobileMoney.notConfigured.description')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* OHADA Compliance Section */}
      <div className="bg-surface border border-border-subtle rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
              <Building className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('invoiceSettings.ohada.title')}</h2>
              <p className="text-xs text-muted">{t('invoiceSettings.ohada.subtitle')}</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showOhadaSection ?? true}
              onChange={(e) => handleInputChange('showOhadaSection', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-muted/30 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-500"></div>
            <span className="ms-3 text-sm font-medium text-muted flex items-center gap-1">
              {formData.showOhadaSection ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {formData.showOhadaSection ? t('invoiceSettings.visibility.visible') : t('invoiceSettings.visibility.hidden')}
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.companyName')}</label>
            <input
              type="text"
              value={formData.companyName || ''}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.legalForm')}</label>
            <input
              type="text"
              value={formData.legalForm || ''}
              onChange={(e) => handleInputChange('legalForm', e.target.value)}
              placeholder={t('invoiceSettings.ohada.legalFormPlaceholder')}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.capitalAmount')}</label>
            <input
              type="number"
              value={formData.capitalAmount || ''}
              onChange={(e) => handleInputChange('capitalAmount', e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.currency')}</label>
            <input
              type="text"
              value={formData.capitalCurrency || ''}
              onChange={(e) => handleInputChange('capitalCurrency', e.target.value)}
              maxLength={3}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.rccmNumber')}</label>
            <input
              type="text"
              value={formData.rccmNumber || ''}
              onChange={(e) => handleInputChange('rccmNumber', e.target.value)}
              placeholder={t('invoiceSettings.ohada.rccmPlaceholder')}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.placeOfRegistration')}</label>
            <input
              type="text"
              value={formData.placeOfRegistration || ''}
              onChange={(e) => handleInputChange('placeOfRegistration', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.taxId')}</label>
            <input
              type="text"
              value={formData.taxId || ''}
              onChange={(e) => handleInputChange('taxId', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.email')}</label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.phone')}</label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.website')}</label>
            <input
              type="url"
              value={formData.website || ''}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.address')}</label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.city')}</label>
            <input
              type="text"
              value={formData.city || ''}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.country')}</label>
            <input
              type="text"
              value={formData.country || ''}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.ohada.postalCode')}</label>
            <input
              type="text"
              value={formData.postalCode || ''}
              onChange={(e) => handleInputChange('postalCode', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        </div>
      </div>

      {/* Signature Section */}
      <div className="bg-surface border border-border-subtle rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('invoiceSettings.signature.title')}</h2>
              <p className="text-xs text-muted">{t('invoiceSettings.signature.subtitle')}</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showSignature ?? true}
              onChange={(e) => handleInputChange('showSignature', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-muted/30 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-500"></div>
            <span className="ms-3 text-sm font-medium text-muted flex items-center gap-1">
              {formData.showSignature ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {formData.showSignature ? t('invoiceSettings.visibility.visible') : t('invoiceSettings.visibility.hidden')}
            </span>
          </label>
        </div>

        <div className="space-y-4">
          {/* Signature Image Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.signature.imageLabel')}</label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="signature-upload"
              />
              <label
                htmlFor="signature-upload"
                className="flex items-center gap-2 px-4 py-2 border border-border-subtle rounded-lg text-sm font-medium cursor-pointer hover:bg-hover"
              >
                <Upload className="w-4 h-4" />
                {t('invoiceSettings.signature.chooseImage')}
              </label>
              {selectedFile && (
                <button
                  type="button"
                  onClick={handleUploadSignature}
                  disabled={uploadSignatureMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {uploadSignatureMutation.isPending ? t('invoiceSettings.signature.uploading') : t('invoiceSettings.signature.upload')}
                </button>
              )}
              {settings?.signatureImagePath && !selectedFile && (
                <button
                  type="button"
                  onClick={handleDeleteSignature}
                  disabled={deleteSignatureMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-red-500 border border-red-500/30 rounded-lg text-sm font-medium hover:bg-red-500/10 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('invoiceSettings.signature.delete')}
                </button>
              )}
            </div>
            {(filePreview || settings?.signatureImagePath) && (
              <div className="mt-3 p-4 bg-background border border-border-subtle rounded-lg">
                <img
                  src={filePreview || `/api/v1/${settings?.signatureImagePath}`}
                  alt="Signature"
                  className="max-h-32 object-contain"
                />
              </div>
            )}
          </div>

          {/* Signature Text Alternative */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.signature.textLabel')}</label>
            <input
              type="text"
              value={formData.signatureText || ''}
              onChange={(e) => handleInputChange('signatureText', e.target.value)}
              placeholder={t('invoiceSettings.signature.textPlaceholder')}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Signatory Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.signature.signatoryName')}</label>
              <input
                type="text"
                value={formData.signatoryName || ''}
                onChange={(e) => handleInputChange('signatoryName', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('invoiceSettings.signature.signatoryTitle')}</label>
              <input
                type="text"
                value={formData.signatoryTitle || ''}
                onChange={(e) => handleInputChange('signatoryTitle', e.target.value)}
                placeholder={t('invoiceSettings.signature.signatoryTitlePlaceholder')}
                className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Notes - Multilingual */}
      <div className="bg-surface border border-border-subtle rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <Globe className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('invoiceSettings.footerNotes.title')}</h2>
            <p className="text-xs text-muted">{t('invoiceSettings.footerNotes.subtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* French Footer Notes */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-5 text-xs font-bold bg-blue-500/20 text-blue-400 rounded">FR</span>
              <label className="text-sm font-medium text-foreground">{t('invoiceSettings.footerNotes.labelFr')}</label>
            </div>
            <textarea
              value={formData.footerNotesFr || ''}
              onChange={(e) => handleInputChange('footerNotesFr', e.target.value)}
              rows={4}
              placeholder={t('invoiceSettings.footerNotes.placeholderFr')}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          {/* English Footer Notes */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-5 text-xs font-bold bg-emerald-500/20 text-emerald-400 rounded">EN</span>
              <label className="text-sm font-medium text-foreground">{t('invoiceSettings.footerNotes.labelEn')}</label>
            </div>
            <textarea
              value={formData.footerNotesEn || ''}
              onChange={(e) => handleInputChange('footerNotesEn', e.target.value)}
              rows={4}
              placeholder={t('invoiceSettings.footerNotes.placeholderEn')}
              className="w-full px-3 py-2 bg-background border border-border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {updateMutation.isPending ? t('invoiceSettings.actions.saving') : t('invoiceSettings.actions.save')}
        </button>
      </div>
    </form>
  );
}
